<template>
  <div id="lisl-generator">
    <header class="app-header" @click="$router.push('/')" style="cursor: pointer;" title="Zurück zur Startseite">
      <img alt="LiSL" src="./assets/logos/logo_small.svg" class="logo" />
      <h1>Kachelgenerator</h1>
      <a href="https://lisl-deutschland.de/impressum" target="_blank" rel="noopener noreferrer" class="impressum-link" @click.stop>Impressum</a>
    </header>
    <main class="main-content">
      <router-view/>
    </main>
  </div>
</template>

<style>
/* Font Registrations */
@font-face {
  font-family: 'Barlow';
  src: url('./assets/fonts/Barlow.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'BarlowCondensed';
  src: url('./assets/fonts/BarlowCondensed-SemiBold.ttf') format('truetype');
  font-weight: 600;
  font-style: normal;
}

/* Global Variables */
:root {
  --c-magenta: #e5007d;
  --c-yellow: #ffed00;
  --c-cyan: #009ee3;
  --c-purple: #a5027d;
  --c-white: #ffffff;
  --c-black: #000000;
  --c-light-grey: #f4f4f4;
  --c-medium-light-grey: #dddddd;
  --c-grey: #888888;
  --c-dark-grey: #333333;
  --c-transparent: transparent;
  --font-body: 'Barlow', Arial, sans-serif;
  --font-heading: 'BarlowCondensed', 'Barlow', Arial, sans-serif;
}

/* CSS Reset & Basics */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

a {
  color: var(--c-magenta);
  text-decoration: none;
}
a:hover {
  color: var(--c-purple);
  text-decoration: underline;
}

body {
  font-family: var(--font-body);
  background-color: var(--c-light-grey);
  color: var(--c-dark-grey);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#lisl-generator {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
}

.app-header {
  background-color: var(--c-white);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  padding: 10px calc(max(100vw - 600px, 0px) / 2 + 20px);
  gap: 15px;
  position: sticky;
  top: 0;
  z-index: 100;
  user-select: none;
  justify-content: start;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
}

.app-header::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

.app-header .logo {
  height: 35px;
  width: auto;
  max-width: 50%;
  object-fit: contain;
}

.app-header h1 {
  font-family: var(--font-heading);
  font-size: 1.3rem;
  color: var(--c-magenta);
  text-transform: uppercase;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

/* Common Styles */
.step-container {
  padding: 20px;
  padding-bottom: 100px; /* space for sticky nav */
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  user-select: none;
}

.step-title {
  font-family: var(--font-heading);
  font-size: 1.8rem;
  text-transform: uppercase;
  color: var(--c-purple);
}

.option-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.selection-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background-color: var(--c-white);
  border-radius: 12px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  user-select: none;
}

.selection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.08);
}

.selection-card.active {
  border-color: var(--c-magenta);
  background-color: #fdf2f8; /* light magenta tint */
}

.check-icon {
  margin-left: auto;
  color: var(--c-magenta);
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}

.selection-info h3 {
  font-family: var(--font-heading);
  text-transform: uppercase;
  font-size: 1.2rem;
  margin-bottom: 4px;
  color: var(--c-dark-grey);
}

.selection-info p {
  font-size: 0.9rem;
  color: #666;
}

.selection-icon {
  width: 60px;
  height: 60px;
  object-fit: contain;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  background-color: var(--c-light-grey);
  border-radius: 8px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  background-color: var(--c-white);
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.input-group label {
  font-family: var(--font-heading);
  text-transform: uppercase;
  color: var(--c-cyan);
  font-size: 1.3rem;
  user-select: none;
}

ul {
  list-style-position: inside;
  margin-left: 10px;
}

li {
  margin-bottom: 8px;
  line-height: 1.3;
}

input, textarea, .styled-select {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-family: var(--font-body);
  font-size: 1rem;
  resize: vertical;
}

input[type=range] {
  padding: 0;
}

input[type=checkbox] {
  padding: 0;
  height: 1.5rem;
  width: 1.5rem;
  accent-color: var(--c-magenta);
}

input:focus, textarea:focus, .styled-select:focus {
  outline: none;
  border-color: var(--c-magenta);
  box-shadow: 0 0 4px var(--c-magenta);
}

.hint-text {
  font-size: 0.85rem;
  color: #666;
  align-items: center;
}

.input-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.impressum-link {
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--c-grey);
  white-space: nowrap;
  text-decoration: none !important;
}

.impressum-link:hover {
  color: var(--c-magenta);
}

</style>
